import { Box, Card, CardContent, Chip, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { AnalysisScope, type MessageAnalysisResult } from '@k-ucap/shared';
import { getSeverityColor } from './utils.js';

export interface AnalysisSummaryProps {
  result: MessageAnalysisResult | null;
  autoAnalyze: boolean;
  autoFollow: boolean;
  onToggleAutoAnalyze: () => void;
  onToggleAutoFollow: () => void;
  totalEvents: number;
  passCount: number;
  warnCount: number;
  failCount: number;
  unknownCount: number;
  unsupportedCount: number;
}

export const AnalysisSummary = ({
  result,
  autoAnalyze,
  autoFollow,
  onToggleAutoAnalyze,
  onToggleAutoFollow,
  totalEvents,
  passCount,
  warnCount,
  failCount,
  unknownCount,
  unsupportedCount,
}: AnalysisSummaryProps) => {
  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h4">메시지 분석기</Typography>
            {result && (
              <Chip
                label={result.overallStatus}
                size="medium"
                color={getSeverityColor(result.overallStatus)}
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: { md: 'flex-end' }, gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={<Switch checked={autoAnalyze} onChange={onToggleAutoAnalyze} />}
              label="Auto Analyze"
            />
            <FormControlLabel
              control={<Switch checked={autoFollow} onChange={onToggleAutoFollow} />}
              label="Auto Follow"
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">Total Events</Typography>
              <Typography variant="h4">{totalEvents}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">PASS</Typography>
              <Typography variant="h4" color="success.main">
                {passCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">WARN</Typography>
              <Typography variant="h4" color="warning.main">
                {warnCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">FAIL</Typography>
              <Typography variant="h4" color="error.main">
                {failCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">UNKNOWN</Typography>
              <Typography variant="h4" color="info.main">
                {unknownCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary">UNSUPPORTED</Typography>
              <Typography variant="h4" color="text.secondary">
                {unsupportedCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {result && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Selected:
              </Typography>
              <Chip label={`#${result.messageType}`} size="small" />
              <Chip label={result.messageName} size="small" />
              <Chip label={AnalysisScope.DEFINITION} size="small" color={getSeverityColor(result.definitionStatus)} />
              <Chip label={`PASS ${result.summary.pass}`} size="small" color="success" />
              <Chip label={`WARN ${result.summary.warn}`} size="small" color="warning" />
              <Chip label={`FAIL ${result.summary.fail}`} size="small" color="error" />
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
